import React from 'react';

import type { Text } from '@zillow/constellation';
import { AdornedInput, Adornment, FormField, Label } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { HTMLElementEvent } from '../../../../models/Types';
import type { Section } from '../../../../models/index';
import { TRASH_REMOVAL } from '../../../../constants';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import HeaderToggle from './HeaderToggle';
import { CURRENCY_INPUT_PLACEHOLDER, UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';
import RadioListBox from '../../../Widgets/RadioListBox';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function TrashRemoval_v5({ section, setSectionValues }: Props) {
  const trashRemoval_isEnabled = section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.IS_ENABLED);
  const landlordSelected = section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const fixedAmountValue = section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.FIXED_AMOUNT_VALUE);

  function onToggleUtility() {
    setSectionValues({
      [TRASH_REMOVAL.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.IS_ENABLED),
    });
  }

  function setFixedAmountValue(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [TRASH_REMOVAL.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Trash removal"
        subheader="Tenant pays in addition to base rent"
        switchOn={section.getFieldValue(TRASH_REMOVAL.FIELD_NAME.IS_ENABLED)}
        onSwitchToggle={onToggleUtility}
      />
      {trashRemoval_isEnabled && (
        <>
          <CenteredHR />
          <FormField
            label={<Label>Who will be responsible for trash removal set up?</Label>}
            control={
              <RadioListBox
                section={section}
                setSectionValues={setSectionValues}
                option1FieldName={TRASH_REMOVAL.FIELD_NAME.TENANT_RESPONSIBILITY}
                option2FieldName={TRASH_REMOVAL.FIELD_NAME.LANDLORD_RESPONSIBILITY}
                option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                option1Content={UTILITIES_HEADING_AND_CONTENT.option1Content}
                option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
              />
            }
          />
          {landlordSelected && (
            <FormField
              marginTop="sm"
              label={<Label>What is the additional fixed fee for trash removal?</Label>}
              control={
                <AdornedInput
                  leftAdornment={<Adornment>$</Adornment>}
                  rightAdornment={<Adornment>per month</Adornment>}
                  input={
                    <CurrencyInput
                      aria-label="fixed amount"
                      data-test="fixedAmountValue"
                      placeholder={CURRENCY_INPUT_PLACEHOLDER}
                      value={fixedAmountValue || ''}
                      onBlur={setFixedAmountValue}
                      onChange={setFixedAmountValue}
                    />
                  }
                />
              }
            />
          )}
        </>
      )}
    </>
  );
}

TrashRemoval_v5.description = 'Get rid of service provider, as it is asked elsewhere.';
